/**
 * TileTitle - Standalone component for rendering tile titles/labels
 *
 * Renders appropriate title based on the dimension being displayed:
 * - 'run': Displays a link to the run with its display name
 * - 'step': Displays "Step {value}"
 * - 'index': Displays "Index {value}"
 */

import React from 'react';
import {twMerge} from 'tailwind-merge';

import {RunSignature} from '../../../types/run';
import {SingleRunLink} from '../../MultiRunWorkspace/SingleRunMode';
import {GridCell, TileDimension} from '../layout/types';

export type TileTitleProps = {
  dimension: TileDimension;
  cell: GridCell;
  disableRunLinks?: boolean;
  className?: string;

  /** Optional test identifier */
  'data-test'?: string;
};

/**
 * Renders a tile title based on the specified dimension.
 *
 * @param props - TileTitleProps
 * @returns React element
 */
export const TileTitle: React.FC<TileTitleProps> = ({
  dimension,
  cell,
  disableRunLinks = false,
  className,
  'data-test': dataTest = 'tile-title',
}) => {
  if (cell.isEmpty) {
    return null;
  }

  const renderContent = () => {
    switch (dimension) {
      case 'run': {
        const displayName = cell.run.displayName || cell.run.name;
        const runColor = cell.run.color;
        const classNames = twMerge(
          'text__single-line night-aware block text-[10px]',
          className
        );

        if (disableRunLinks) {
          return (
            <span
              className={classNames}
              style={{color: runColor}}
              title={displayName}>
              {displayName}
            </span>
          );
        }

        const runSignature: RunSignature = {
          entityName: cell.run.entityName,
          projectName: cell.run.projectName,
          runName: cell.run.name,
        };

        return (
          <span
            className={classNames}
            style={{color: runColor}}
            title={displayName}>
            <SingleRunLink {...runSignature} linkText={displayName} />
          </span>
        );
      }

      case 'step': {
        return <span className={className}>Step {cell.step}</span>;
      }

      case 'index': {
        return <span className={className}>Index {cell.mediaIndex}</span>;
      }

      default:
        return null;
    }
  };

  return (
    <div className={twMerge('w-full min-w-0', className)} data-test={dataTest}>
      {renderContent()}
    </div>
  );
};
